import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import MainLayout from './components/MainLayout';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import Portfolio from './pages/Portfolio';
import AddAsset from './pages/AddAsset';
import DovizPage from './pages/DovizPage';
import AltinPage from './pages/AltinPage';
import ParitePage from './pages/ParitePage';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import Privacy from './pages/Privacy';
import Settings from './pages/Settings';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminSendEmail from './pages/AdminSendEmail';
import ChangelogManagement from './pages/ChangelogManagement';
import SourceComparison from './pages/SourceComparison';
import AlertsPage from './pages/AlertsPage';
import ChangelogModal from './components/ChangelogModal';
import axios from 'axios';

// Production API URL ayarla
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  // Auth yüklenene kadar bekle
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// Admin Route wrapper
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  // Auth yüklenene kadar bekle
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/" />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CurrencyProvider>
          <Router>
            <ScrollToTop />
            <ToastContainer />
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300 ease-in-out">
              <Navbar />
              <Routes>
                <Route path="/" element={<MainLayout><Home /></MainLayout>} />
                <Route path="/doviz" element={<MainLayout><DovizPage /></MainLayout>} />
                <Route path="/altin" element={<MainLayout><AltinPage /></MainLayout>} />
                <Route path="/parite" element={<MainLayout><ParitePage /></MainLayout>} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:resetToken" element={<ResetPassword />} />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/verify-email"
                  element={
                    <ProtectedRoute>
                      <VerifyEmail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/portfolio"
                  element={
                    <ProtectedRoute>
                      <Portfolio />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/add-asset"
                  element={
                    <ProtectedRoute>
                      <AddAsset />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/dashboard"
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <AdminRoute>
                      <UserManagement />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/email"
                  element={
                    <AdminRoute>
                      <AdminSendEmail />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/alerts"
                  element={
                    <ProtectedRoute>
                      <AlertsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/changelog"
                  element={
                    <AdminRoute>
                      <ChangelogManagement />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/source-comparison"
                  element={
                    <AdminRoute>
                      <SourceComparison />
                    </AdminRoute>
                  }
                />
              </Routes>
              <ChangelogModal />
              <Footer />
            </div>
          </Router>
        </CurrencyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
